/**
 * TeamsPhase — the "Teams" tab of a tournament.
 *
 * Everyone sees the teams, their owners and squads. Admins also choose how
 * players are distributed (Auction / Manual / Auto-balance), create teams (by
 * hand or from team-owner registrations), link owners, mark captains and — in
 * the non-auction methods — fill squads one by one, in bulk, by auto-balance
 * or from a CSV. Backed by /v1/events/{id}/team-setup/*.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { axiosInstance } from '../../config/axios';
import { toast } from 'sonner';
import DisqualifyDialog from './DisqualifyDialog';

const METHOD_INFO = {
  auction: {
    label: 'Auction',
    icon: 'ph-gavel',
    desc: 'Team owners bid for players in a live auction (Auction tab).',
  },
  manual: {
    label: 'Manual',
    icon: 'ph-hand-pointing',
    desc: 'Admins put registered players on teams directly.',
  },
  auto_balance: {
    label: 'Auto-balance',
    icon: 'ph-scales',
    desc: 'The app deals players out by role (and A/B/C category); you preview and adjust.',
  },
  team_registration: {
    label: 'Team registration',
    icon: 'ph-users-three',
    desc: 'Captains register whole teams and invite their players by link; you approve each team.',
  },
};

const REG_STATUS = {
  draft: 'bg-gray-100 text-gray-600',
  submitted: 'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-600',
};
const rupees = (paise) => `₹${((paise || 0) / 100).toLocaleString('en-IN')}`;

const ROLE_ORDER = ['Wicket-keeper', 'All-rounder', 'Bowler', 'Batter', 'Unspecified'];
const roleShort = (r) => ({ 'Wicket-keeper': 'WK', 'All-rounder': 'AR', Bowler: 'BOWL', Batter: 'BAT' }[r] || '—');

const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
};

export default function TeamsPhase({ event }) {
  // Admin tools only for this event's admins (owner, co-admins, Super Admins)
  const isAdmin = !!event?.can_manage;

  const [teams, setTeams] = useState([]);
  const [overview, setOverview] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [squads, setSquads] = useState({});
  const expandedRef = useRef(null);   // read by load() without re-creating it
  expandedRef.current = expanded;
  const [busy, setBusy] = useState(false);
  const [dqTeam, setDqTeam] = useState(null);   // team being disqualified (dialog open)
  const [newTeam, setNewTeam] = useState(null); // null = closed, string = name being typed
  const [selected, setSelected] = useState([]);   // registration ids ticked for bulk add
  const [bulkTeam, setBulkTeam] = useState('');
  const [balance, setBalance] = useState(null);   // auto-balance preview
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvText, setCsvText] = useState('');
  const [teamRegs, setTeamRegs] = useState([]);   // "bring your own team" registrations (admin)
  const [openReg, setOpenReg] = useState(null);

  const base = `/v1/events/${event.id}/team-setup`;

  const load = useCallback(async () => {
    try {
      const teamsRes = await axiosInstance.get(`${base}/teams`);
      setTeams(teamsRes.data);
      if (isAdmin) {
        const ov = await axiosInstance.get(`${base}/overview`);
        setOverview(ov.data);
        if (ov.data.method === 'team_registration' || ov.data.team_registrations_waiting) {
          const tr = await axiosInstance.get(`/v1/events/${event.id}/team-registrations`);
          setTeamRegs(tr.data);
        } else {
          setTeamRegs([]);
        }
        if (ov.data.method !== 'auction') {
          const pl = await axiosInstance.get(`${base}/available-players`);
          setPlayers(pl.data);
        } else {
          setPlayers([]);
        }
      }
      setSquads({});
      setSelected([]);
      // Squads were just invalidated — refetch the one that's open
      const open = expandedRef.current;
      if (open && teamsRes.data.some((t) => t.id === open)) {
        axiosInstance.get(`${base}/teams/${open}/squad`)
          .then((res) => setSquads((s) => ({ ...s, [open]: res.data })))
          .catch(() => {});
      }
    } catch (e) {
      toast.error(errMsg(e, 'Failed to load teams'));
    } finally {
      setLoading(false);
    }
  }, [base, isAdmin, event.id]);

  useEffect(() => { load(); }, [load]);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      const res = await fn();
      if (okMsg) toast.success(typeof okMsg === 'function' ? okMsg(res) : okMsg);
      await load();
      return res;
    } catch (e) {
      toast.error(errMsg(e, 'Something went wrong'));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const toggleSquad = async (teamId) => {
    if (expanded === teamId) { setExpanded(null); return; }
    setExpanded(teamId);
    try {
      const res = await axiosInstance.get(`${base}/teams/${teamId}/squad`);
      setSquads((s) => ({ ...s, [teamId]: res.data }));
    } catch { /* squad stays empty */ }
  };

  const changeMethod = async (method) => {
    if (method === overview?.method) return;
    const label = METHOD_INFO[method].label;
    if (!window.confirm(`Switch to "${label}"? ${method === 'auction'
      ? 'The Auction tab will be switched on.'
      : 'The Auction tab will be switched off.'}`)) return;
    const res = await run(() => axiosInstance.put(`${base}/method`, { method }), `Players now join teams by: ${label}`);
    if (res) window.location.reload(); // tab list depends on the auction phase
  };

  const createFromOwners = () => run(
    () => axiosInstance.post(`${base}/teams/from-owners`),
    (res) => {
      const d = res.data;
      const parts = [`${d.created_teams.length} team(s) created`, `${d.owners_linked} owner(s) linked`];
      if (d.skipped_without_team_name.length) parts.push(`${d.skipped_without_team_name.length} skipped (no team name)`);
      return parts.join(' · ');
    },
  );

  const addTeam = async () => {
    const name = (newTeam || '').trim();
    if (!name) return;
    const res = await run(() => axiosInstance.post(`${base}/teams`, { team_name: name }), `${name} created`);
    if (res) setNewTeam(null);
  };

  const renameTeam = (team) => {
    const name = window.prompt('New team name', team.team_name);
    if (!name || name.trim() === team.team_name) return;
    run(() => axiosInstance.patch(`${base}/teams/${team.id}`, { team_name: name.trim() }), 'Team renamed');
  };

  const deleteTeam = (team) => {
    if (!window.confirm(`Delete "${team.team_name}"? Its players go back to unassigned and its owners are unlinked.`)) return;
    run(() => axiosInstance.delete(`${base}/teams/${team.id}`), `${team.team_name} deleted`);
  };

  const linkOwner = (team, registrationId) => {
    if (!registrationId) return;
    run(() => axiosInstance.post(`${base}/teams/${team.id}/owners`, { registration_id: registrationId }), (r) => r.data.message);
  };

  const unlinkOwner = (team, owner) => {
    if (!window.confirm(`Unlink ${owner.name} from ${team.team_name}?`)) return;
    run(() => axiosInstance.delete(`${base}/teams/${team.id}/owners/${owner.owner_id}`), 'Owner unlinked');
  };

  const assignPlayer = (registrationId, teamId) => {
    if (!teamId) return;
    run(() => axiosInstance.post(`${base}/assign-player`, { registration_id: registrationId, team_id: teamId }), (r) => r.data.message);
  };

  const removePlayer = (poolId) =>
    run(() => axiosInstance.post(`${base}/remove-player`, { player_pool_id: poolId }), 'Player removed from team');

  const bulkAssign = async () => {
    if (!bulkTeam || selected.length === 0) return;
    await run(() => axiosInstance.post(`${base}/assign-players`, { team_id: bulkTeam, registration_ids: selected }),
      (r) => r.data.message);
    setBulkTeam('');
  };

  const toggleSelected = (id) =>
    setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const previewBalance = async (seed) => {
    setBusy(true);
    try {
      const res = await axiosInstance.post(`${base}/auto-balance/preview`, seed ? { seed } : {});
      setBalance(res.data);
    } catch (e) {
      toast.error(errMsg(e, 'Could not build a preview'));
    } finally {
      setBusy(false);
    }
  };

  const applyBalance = async () => {
    if (!balance?.assignments?.length) return;
    const res = await run(() => axiosInstance.post(`${base}/auto-balance/apply`, { assignments: balance.assignments }),
      (r) => r.data.message);
    if (res) setBalance(null);
  };

  const readCsvFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ''));
    reader.readAsText(file);
  };

  const importCsv = async (dryRun) => {
    if (!csvText.trim()) return;
    setBusy(true);
    try {
      const res = await axiosInstance.post(`${base}/import-roster`, { csv_text: csvText, dry_run: dryRun });
      if (dryRun) {
        setCsvPreview(res.data);
      } else {
        toast.success(`${res.data.placed} player(s) placed${res.data.summary.errors ? ` · ${res.data.summary.errors} row(s) skipped with errors` : ''}`);
        setCsvPreview(null);
        setCsvText('');
        await load();
      }
    } catch (e) {
      toast.error(errMsg(e, 'Import failed'));
    } finally {
      setBusy(false);
    }
  };

  const setCaptain = (team, registrationId, which) => {
    const cur = { captain_registration_id: team.captain_registration_id || null,
                  vice_captain_registration_id: team.vice_captain_registration_id || null };
    const key = which === 'C' ? 'captain_registration_id' : 'vice_captain_registration_id';
    const other = which === 'C' ? 'vice_captain_registration_id' : 'captain_registration_id';
    const next = { ...cur, [key]: cur[key] === registrationId ? null : registrationId };
    if (next[other] === registrationId) next[other] = null;   // one person can't hold both
    run(() => axiosInstance.put(`${base}/teams/${team.id}/captain`, next), 'Captains updated')
      .then(() => toggleSquadReload(team.id));
  };

  const reviewTeam = (reg, decision) => {
    let notes = null;
    if (decision !== 'approve') {
      notes = window.prompt(decision === 'reject'
        ? `Reject ${reg.team_name}? Reason (shown to the registrant):`
        : `What should ${reg.registrant.name} change? (shown to them)`, '');
      if (notes === null) return;
    } else if (!window.confirm(`Approve ${reg.team_name} with ${reg.roster.length} players?`)) {
      return;
    }
    run(() => axiosInstance.post(`/v1/events/${event.id}/team-registrations/${reg.id}/review`, { decision, notes }),
      { approve: `${reg.team_name} approved`, reject: `${reg.team_name} rejected`, request_changes: 'Sent back for changes' }[decision]);
  };

  const disqualify = (team) => setDqTeam(team);

  const confirmDisqualify = async ({ reason, mode }) => {
    const team = dqTeam;
    const res = await run(() => axiosInstance.post(`${base}/teams/${team.id}/disqualify`, { reason, mode }),
      (r) => r.data.message);
    if (res) setDqTeam(null);
  };

  const reinstate = (team) => {
    if (!window.confirm(`Reinstate ${team.team_name}? Matches its disqualification changed will be put back, unless they've been changed since.`)) return;
    run(() => axiosInstance.post(`${base}/teams/${team.id}/reinstate`), (r) => r.data.message);
  };

  const toggleSquadReload = async (teamId) => {
    try {
      const res = await axiosInstance.get(`${base}/teams/${teamId}/squad`);
      setSquads((s) => ({ ...s, [teamId]: res.data }));
      setExpanded(teamId);
    } catch { /* ignore */ }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#3AAFA9] border-t-transparent" />
      </div>
    );
  }

  const method = overview?.method;
  const limits = overview?.limits || {};
  const editable = isAdmin && overview?.teams_editable;
  const manual = isAdmin && !!method && method !== 'auction';
  const unlinkedOwners = (overview?.owners || []).filter((o) => o.linkable && !o.linked_team_id);
  const unassigned = players.filter((p) => !p.assigned_team_id);
  const openTeams = teams.filter((t) => t.remaining_slots > 0);

  return (
    <div className="space-y-5" data-testid="teams-phase">
      {/* ── Admin: distribution method ─────────────────────────── */}
      {isAdmin && overview && (
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100" data-testid="teams-method-card">
          <h3 className="font-extrabold text-[#2B3A67] mb-1">How do players join teams?</h3>
          <p className="text-xs text-gray-500 mb-3">
            {overview.can_change_method
              ? 'Choose before placing any players. You can change it until then.'
              : `Locked — ${overview.players_placed} player(s) are already on teams.`}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {overview.methods_available.map((m) => {
              const info = METHOD_INFO[m];
              const active = m === method;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => changeMethod(m)}
                  disabled={busy || active || !overview.can_change_method}
                  className={`text-left rounded-xl border-2 p-3 transition-colors ${
                    active ? 'border-[#3AAFA9] bg-[#3AAFA9]/10' : 'border-gray-200 hover:border-gray-300'
                  } disabled:cursor-default ${!active && !overview.can_change_method ? 'opacity-50' : ''}`}
                  data-testid={`teams-method-${m}`}
                >
                  <p className="font-bold text-sm text-[#2B3A67] flex items-center gap-2">
                    <i className={`ph-bold ${info.icon}`} /> {info.label}
                    {active && <span className="ml-auto text-[10px] uppercase tracking-wide text-[#3AAFA9]">Selected</span>}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">{info.desc}</p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Admin: team owners ─────────────────────────────────── */}
      {isAdmin && overview?.owners?.length > 0 && (
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100" data-testid="teams-owners-card">
          <div className="flex items-center justify-between gap-3 mb-3">
            <h3 className="font-extrabold text-[#2B3A67]">Team owners</h3>
            {editable && unlinkedOwners.length > 0 && (
              <button
                onClick={createFromOwners}
                disabled={busy}
                className="px-3 py-2 rounded-xl bg-[#2B3A67] text-white font-bold text-xs disabled:opacity-50"
                data-testid="create-teams-from-owners"
              >
                <i className="ph-bold ph-magic-wand mr-1" />
                Create teams from {unlinkedOwners.length} owner{unlinkedOwners.length > 1 ? 's' : ''}
              </button>
            )}
          </div>
          <div className="space-y-2">
            {overview.owners.map((o) => (
              <div key={o.registration_id} className="flex items-center gap-2 text-sm">
                <i className="ph-bold ph-user-circle text-gray-400" />
                <span className="font-bold truncate">{o.owner_name}</span>
                <span className="text-gray-400 truncate">· {o.registered_team_name || 'no team name'}</span>
                <span className="ml-auto shrink-0">
                  {o.payment_pending && (
                    <span className="text-xs font-bold text-orange-700 bg-orange-50 px-2 py-0.5 rounded-full mr-1">Payment pending</span>
                  )}
                  {o.linked_team_name ? (
                    <span className="text-xs font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded-full">→ {o.linked_team_name}</span>
                  ) : o.linkable ? (
                    <span className="text-xs font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">Not linked</span>
                  ) : (
                    <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{o.status.replace(/_/g, ' ')}</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {dqTeam && (
        <DisqualifyDialog eventId={event.id} team={dqTeam} busy={busy}
          onClose={() => setDqTeam(null)} onConfirm={confirmDisqualify} />
      )}

      {/* ── Admin: "bring your own team" registrations ─────────── */}
      {isAdmin && teamRegs.length > 0 && (
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100" data-testid="team-registrations-card">
          <h3 className="font-extrabold text-[#2B3A67] mb-1">
            Team registrations
            {overview?.team_registrations_waiting > 0 && (
              <span className="ml-2 text-xs font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                {overview.team_registrations_waiting} waiting
              </span>
            )}
          </h3>
          <p className="text-xs text-gray-500 mb-3">Teams registered by captains. Approving one adds it, with its players, to the teams below.</p>
          <div className="space-y-2">
            {teamRegs.map((r) => (
              <div key={r.id} className="rounded-xl border border-gray-100" data-testid={`team-reg-${r.id}`}>
                <div className="flex items-center gap-2 p-3 cursor-pointer" onClick={() => setOpenReg(openReg === r.id ? null : r.id)}>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold truncate">{r.team_name}</p>
                    <p className="text-xs text-gray-500 truncate">
                      by {r.registrant.name} · {r.roster.length}/{r.squad_max} players ·{' '}
                      {r.fee_mode === 'team'
                        ? (r.team_fee_amount ? `team fee ${rupees(r.team_fee_amount)} ${({ paid: 'paid', partially_refunded: 'paid, partly refunded', refunded: 'refunded' })[r.payment_status] || 'unpaid'}` : 'free')
                        : (r.player_fee_amount ? `${rupees(r.player_fee_amount)} per player` : 'free')}
                    </p>
                  </div>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full shrink-0 ${REG_STATUS[r.status] || ''}`}>{r.status}</span>
                </div>
                {openReg === r.id && (
                  <div className="border-t border-gray-100 p-3 space-y-3">
                    <ul className="space-y-1 text-sm">
                      {r.roster.map((p) => (
                        <li key={p.registration_id} className="flex items-center gap-2">
                          <span className="truncate flex-1">{p.player_name}</span>
                          <span className="text-[10px] font-bold text-gray-500 bg-gray-100 px-1.5 rounded">{roleShort(p.role)}</span>
                          {p.is_captain && <span className="text-[10px] font-bold bg-[#E54562] text-white px-1.5 rounded">C</span>}
                        </li>
                      ))}
                      {r.roster.length === 0 && <li className="text-xs text-gray-400">No players yet.</li>}
                    </ul>
                    {r.review_notes && <p className="text-xs text-gray-500">Last note: {r.review_notes}</p>}
                    {r.status === 'submitted' && (
                      <div className="flex flex-wrap gap-2">
                        <button onClick={() => reviewTeam(r, 'approve')} disabled={busy}
                          className="px-3 py-2 rounded-xl bg-green-600 text-white font-bold text-xs" data-testid={`approve-team-reg-${r.id}`}>Approve</button>
                        <button onClick={() => reviewTeam(r, 'request_changes')} disabled={busy}
                          className="px-3 py-2 rounded-xl border border-amber-300 text-amber-700 font-bold text-xs" data-testid={`return-team-reg-${r.id}`}>Send back for changes</button>
                        <button onClick={() => reviewTeam(r, 'reject')} disabled={busy}
                          className="px-3 py-2 rounded-xl border border-red-200 text-red-500 font-bold text-xs" data-testid={`reject-team-reg-${r.id}`}>Reject</button>
                      </div>
                    )}
                    {r.status === 'draft' && (
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-gray-500 flex-1">Still being put together by the registrant.</p>
                        <button onClick={() => reviewTeam(r, 'reject')} disabled={busy}
                          className="px-3 py-2 rounded-xl border border-red-200 text-red-500 font-bold text-xs">Reject</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Teams ───────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between gap-3 mb-4">
          <h3 className="font-extrabold text-[#2B3A67]">
            Teams <span className="text-gray-400 font-bold">{teams.length}{limits.max_teams ? ` / ${limits.max_teams}` : ''}</span>
          </h3>
          {editable && newTeam === null && (
            <button
              onClick={() => setNewTeam('')}
              className="px-3 py-2 rounded-xl border-2 border-[#2B3A67] text-[#2B3A67] font-bold text-xs"
              data-testid="add-team-btn"
            >
              <i className="ph-bold ph-plus mr-1" /> Add team
            </button>
          )}
        </div>

        {isAdmin && method === 'auction' && event.cricket_config?.registration_roles
          && !event.cricket_config.registration_roles.includes('team_owner') && (
          <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mb-3" data-testid="auction-no-owners-warning">
            <i className="ph-bold ph-warning mr-1" />
            Team-owner registration is off for this tournament, so teams will have no owners to bid in the auction.
            Turn it on in the event settings (Edit), or choose another way to form teams.
          </p>
        )}
        {isAdmin && method === 'auction' && (
          <p className="text-xs text-gray-500 mb-3">
            <i className="ph-bold ph-info mr-1" />
            Players join teams through the Auction tab{overview?.auction_status && overview.auction_status !== 'DRAFT'
              ? `. The auction is ${overview.auction_status.toLowerCase()}, so teams can no longer be added or removed.` : '.'}
          </p>
        )}

        {newTeam !== null && (
          <div className="flex gap-2 mb-4">
            <input
              autoFocus
              value={newTeam}
              onChange={(e) => setNewTeam(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addTeam()}
              placeholder="Team name"
              maxLength={100}
              className="flex-1 px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#2B3A67]/30"
              data-testid="new-team-name"
            />
            <button onClick={addTeam} disabled={busy || !newTeam.trim()}
              className="px-4 py-2 rounded-xl bg-[#3AAFA9] text-white font-bold text-sm disabled:opacity-50"
              data-testid="new-team-save">Add</button>
            <button onClick={() => setNewTeam(null)} className="px-3 py-2 rounded-xl border border-gray-200 text-sm">Cancel</button>
          </div>
        )}

        {teams.length === 0 ? (
          <p className="text-center text-sm text-gray-400 py-8">
            {isAdmin ? 'No teams yet. Add one, or create them from team-owner registrations.' : 'Teams will appear here once they are formed.'}
          </p>
        ) : (
          <div className="space-y-3">
            {teams.map((team) => (
              <div key={team.id} className="rounded-xl border border-gray-100" data-testid={`team-card-${team.id}`}>
                <div className="flex items-center gap-3 p-3 cursor-pointer" onClick={() => toggleSquad(team.id)}>
                  {team.team_logo_url ? (
                    <img src={team.team_logo_url} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#2B3A67] text-white font-extrabold flex items-center justify-center shrink-0">
                      {team.team_name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-extrabold truncate">
                      {team.team_name}
                      {team.disqualified && (
                        <span className="ml-2 align-middle text-[10px] font-bold bg-red-100 text-red-600 px-1.5 py-0.5 rounded"
                          title={team.disqualified_reason || ''}>DISQUALIFIED</span>
                      )}
                    </p>
                    {team.registered_by && <p className="text-[11px] text-gray-400">Registered by {team.registered_by}</p>}
                    <div className="flex flex-wrap items-center gap-1 mt-0.5">
                      {team.owners.length === 0 && !team.registered_by && <span className="text-xs text-gray-400">No owner</span>}
                      {team.owners.map((o) => (
                        <span key={o.owner_id} className="inline-flex items-center gap-1 text-xs bg-[#2B3A67]/5 text-[#2B3A67] px-2 py-0.5 rounded-full">
                          <i className="ph-bold ph-crown-simple" /> {o.name}
                          {o.payment_pending && <span className="text-orange-600 font-bold" title="Owner hasn't paid yet">· payment pending</span>}
                          {editable && (
                            <button onClick={(e) => { e.stopPropagation(); unlinkOwner(team, o); }}
                              className="text-gray-400 hover:text-red-500" aria-label={`Unlink ${o.name}`}
                              data-testid={`unlink-owner-${o.owner_id}`}>×</button>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                  <span className="text-xs font-bold text-gray-500 shrink-0">{team.squad_count}/{team.max_squad_size}</span>
                  <i className={`ph-bold ${expanded === team.id ? 'ph-caret-up' : 'ph-caret-down'} text-gray-400`} />
                </div>

                {expanded === team.id && (
                  <div className="border-t border-gray-100 p-3 space-y-3">
                    {isAdmin && (
                      <div className="flex flex-wrap gap-2">
                        {editable && unlinkedOwners.length > 0 && (
                          <select defaultValue="" onChange={(e) => { linkOwner(team, e.target.value); e.target.value = ''; }}
                            className="px-2 py-1.5 rounded-lg border border-gray-200 text-xs" data-testid={`link-owner-${team.id}`}>
                            <option value="">+ Link owner…</option>
                            {unlinkedOwners.map((o) => (
                              <option key={o.registration_id} value={o.registration_id}>
                                {o.owner_name}{o.registered_team_name ? ` (${o.registered_team_name})` : ''}{o.payment_pending ? ' — payment pending' : ''}
                              </option>
                            ))}
                          </select>
                        )}
                        <button onClick={() => renameTeam(team)} className="px-2 py-1.5 rounded-lg border border-gray-200 text-xs font-bold"
                          data-testid={`rename-team-${team.id}`}>Rename</button>
                        {team.disqualified ? (
                          <button onClick={() => reinstate(team)} className="px-2 py-1.5 rounded-lg border border-green-300 text-green-700 text-xs font-bold"
                            data-testid={`reinstate-team-${team.id}`}>Reinstate</button>
                        ) : (
                          <button onClick={() => disqualify(team)} className="px-2 py-1.5 rounded-lg border border-red-200 text-red-500 text-xs font-bold"
                            data-testid={`disqualify-team-${team.id}`}>Disqualify</button>
                        )}
                        {editable && (
                          <button onClick={() => deleteTeam(team)} className="px-2 py-1.5 rounded-lg border border-red-200 text-red-500 text-xs font-bold"
                            data-testid={`delete-team-${team.id}`}>Delete</button>
                        )}
                      </div>
                    )}
                    {team.disqualified && team.disqualified_reason && (
                      <p className="text-xs text-red-600 bg-red-50 rounded-lg px-2 py-1.5">
                        <i className="ph-bold ph-prohibit mr-1" />Disqualified: {team.disqualified_reason}
                        <span className="text-red-400"> · {team.disqualification_mode === 'expunge'
                          ? 'results removed from the standings' : 'unplayed matches awarded to opponents'}</span>
                      </p>
                    )}
                    {!squads[team.id] ? (
                      <p className="text-xs text-gray-400">Loading squad…</p>
                    ) : squads[team.id].length === 0 ? (
                      <p className="text-xs text-gray-400">No players yet.</p>
                    ) : (
                      <ul className="space-y-1.5">
                        {squads[team.id].map((p) => (
                          <li key={p.id} className="flex items-center gap-2 text-sm">
                            {p.player_photo_url
                              ? <img src={p.player_photo_url} alt="" className="w-7 h-7 rounded-full object-cover" />
                              : <i className="ph-bold ph-user-circle text-2xl text-gray-300" />}
                            <span className="font-bold truncate">{p.player_name}</span>
                            {p.playing_role && <span className="text-xs text-gray-400 truncate">{p.playing_role}</span>}
                            {p.is_captain && <span className="text-[10px] font-bold bg-[#E54562] text-white px-1.5 rounded" title="Captain">C</span>}
                            {p.is_vice_captain && <span className="text-[10px] font-bold bg-[#E54562]/15 text-[#E54562] px-1.5 rounded" title="Vice-captain">VC</span>}
                            {p.is_icon && <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-1.5 rounded">ICON</span>}
                            {p.is_owner_player && <span className="text-[10px] font-bold bg-[#2B3A67]/10 text-[#2B3A67] px-1.5 rounded">OWNER</span>}
                            {isAdmin && p.registration_id && (
                              <span className="ml-auto flex items-center gap-1 shrink-0">
                                <button onClick={() => setCaptain(team, p.registration_id, 'C')} disabled={busy}
                                  className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${p.is_captain ? 'border-[#E54562] text-[#E54562]' : 'border-gray-200 text-gray-400'}`}
                                  title={p.is_captain ? 'Remove as captain' : 'Make captain'} data-testid={`make-captain-${p.id}`}>C</button>
                                <button onClick={() => setCaptain(team, p.registration_id, 'VC')} disabled={busy}
                                  className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${p.is_vice_captain ? 'border-[#E54562] text-[#E54562]' : 'border-gray-200 text-gray-400'}`}
                                  title={p.is_vice_captain ? 'Remove as vice-captain' : 'Make vice-captain'} data-testid={`make-vc-${p.id}`}>VC</button>
                                {manual && !p.is_icon && !p.is_owner_player && (
                                  <button onClick={() => removePlayer(p.id)} className="text-xs text-red-500 font-bold ml-1"
                                    data-testid={`remove-player-${p.id}`}>Remove</button>
                                )}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Admin, non-auction methods: tools to fill squads ───── */}
      {manual && (
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 space-y-3" data-testid="fill-squads-card">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-extrabold text-[#2B3A67]">
              Unassigned players <span className="text-gray-400 font-bold">{unassigned.length}</span>
            </h3>
            {unassigned.length > 0 && teams.length > 0 && (
              <button onClick={() => previewBalance()} disabled={busy}
                className={`px-3 py-2 rounded-xl font-bold text-xs disabled:opacity-50 ${
                  method === 'auto_balance' ? 'bg-[#2B3A67] text-white' : 'border-2 border-[#2B3A67] text-[#2B3A67]'}`}
                data-testid="auto-balance-btn">
                <i className="ph-bold ph-scales mr-1" /> Auto-balance by role
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500">Approved player registrations not yet on a team. Tick several to add them together.</p>

          {/* Auto-balance preview */}
          {balance && (
            <div className="rounded-xl border-2 border-[#3AAFA9] bg-[#3AAFA9]/5 p-3 space-y-3" data-testid="auto-balance-preview">
              <p className="text-sm font-bold text-[#2B3A67]">
                Preview — {balance.assignments.length} player(s) dealt out by role. Nothing is saved yet.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {balance.teams.map((t) => (
                  <div key={t.team_id} className="bg-white rounded-lg p-2 border border-gray-100">
                    <p className="font-bold text-sm truncate">{t.team_name}
                      <span className="text-xs text-gray-400 font-normal"> · {t.squad_after} players after</span></p>
                    <p className="text-[11px] text-gray-500 mb-1">
                      {ROLE_ORDER.filter((r) => t.roles_after[r]).map((r) => `${t.roles_after[r]} ${roleShort(r)}`).join(' · ') || 'No players'}
                    </p>
                    <ul className="text-xs space-y-0.5">
                      {t.new_players.map((p) => (
                        <li key={p.registration_id} className="flex gap-1">
                          <span className="font-bold text-[10px] text-gray-400 w-9 shrink-0">{roleShort(p.role)}</span>
                          <span className="truncate">{p.player_name}</span>
                          {p.category !== '-' && <span className="text-gray-400">({p.category})</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              {balance.not_placed.length > 0 && (
                <p className="text-xs text-orange-700">
                  No room for {balance.not_placed.length}: {balance.not_placed.join(', ')}. Add a team or raise squad sizes.
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <button onClick={applyBalance} disabled={busy || balance.assignments.length === 0}
                  className="px-4 py-2 rounded-xl bg-[#3AAFA9] text-white font-bold text-sm disabled:opacity-50"
                  data-testid="auto-balance-apply">Save this split</button>
                <button onClick={() => previewBalance()} disabled={busy}
                  className="px-4 py-2 rounded-xl border border-gray-200 font-bold text-sm" data-testid="auto-balance-shuffle">
                  <i className="ph-bold ph-shuffle mr-1" /> Shuffle again</button>
                <button onClick={() => setBalance(null)} className="px-4 py-2 rounded-xl border border-gray-200 text-sm">Cancel</button>
              </div>
            </div>
          )}

          {/* Bulk add bar */}
          {selected.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 rounded-xl bg-[#2B3A67]/5 p-2" data-testid="bulk-bar">
              <span className="text-sm font-bold text-[#2B3A67]">{selected.length} selected</span>
              <select value={bulkTeam} onChange={(e) => setBulkTeam(e.target.value)}
                className="px-2 py-1.5 rounded-lg border border-gray-200 text-xs" data-testid="bulk-team-select">
                <option value="">Choose team…</option>
                {openTeams.map((t) => (
                  <option key={t.id} value={t.id}>{t.team_name} ({t.remaining_slots} free)</option>
                ))}
              </select>
              <button onClick={bulkAssign} disabled={busy || !bulkTeam}
                className="px-3 py-1.5 rounded-lg bg-[#3AAFA9] text-white font-bold text-xs disabled:opacity-50"
                data-testid="bulk-assign-btn">Add to team</button>
              <button onClick={() => setSelected([])} className="text-xs text-gray-500 ml-auto">Clear</button>
            </div>
          )}

          {unassigned.length === 0 ? (
            <p className="text-center text-sm text-gray-400 py-4">Everyone is on a team.</p>
          ) : teams.length === 0 ? (
            <p className="text-center text-sm text-gray-400 py-4">Create teams first.</p>
          ) : (
            <ul className="space-y-2">
              {unassigned.map((p) => (
                <li key={p.registration_id} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={selected.includes(p.registration_id)}
                    onChange={() => toggleSelected(p.registration_id)} className="w-4 h-4 accent-[#2B3A67]"
                    aria-label={`Select ${p.player_name}`} data-testid={`select-player-${p.registration_id}`} />
                  <span className="font-bold truncate flex-1">{p.player_name}</span>
                  <span className="text-[10px] font-bold text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded shrink-0" title={p.playing_role || 'No role given'}>
                    {roleShort(p.role)}{p.category ? ` · ${p.category}` : ''}
                  </span>
                  <select defaultValue="" disabled={busy}
                    onChange={(e) => { assignPlayer(p.registration_id, e.target.value); e.target.value = ''; }}
                    className="px-2 py-1.5 rounded-lg border border-gray-200 text-xs"
                    data-testid={`assign-select-${p.registration_id}`}>
                    <option value="">Add to team…</option>
                    {openTeams.map((t) => (
                      <option key={t.id} value={t.id}>{t.team_name} ({t.squad_count}/{t.max_squad_size})</option>
                    ))}
                  </select>
                </li>
              ))}
            </ul>
          )}

          {/* CSV roster import */}
          <details className="rounded-xl border border-gray-100 p-3" data-testid="csv-import">
            <summary className="text-sm font-bold text-[#2B3A67] cursor-pointer">
              <i className="ph-bold ph-file-csv mr-1" /> Import rosters from a CSV
            </summary>
            <div className="mt-3 space-y-2">
              <p className="text-xs text-gray-500">
                Two columns: <code>team</code> (exact team name) and <code>email</code> (the player's account email).
                You'll see a preview before anything is saved.
              </p>
              <input type="file" accept=".csv,text/csv" onChange={(e) => readCsvFile(e.target.files?.[0])}
                className="text-xs" data-testid="csv-file" />
              <textarea value={csvText} onChange={(e) => setCsvText(e.target.value)} rows={4}
                placeholder={'team,email\nThunder Kings,player@example.com'}
                className="w-full px-3 py-2 rounded-xl border border-gray-200 text-xs font-mono" data-testid="csv-text" />
              <button onClick={() => importCsv(true)} disabled={busy || !csvText.trim()}
                className="px-3 py-2 rounded-xl border-2 border-[#2B3A67] text-[#2B3A67] font-bold text-xs disabled:opacity-50"
                data-testid="csv-preview-btn">Preview import</button>
              {csvPreview && (
                <div className="space-y-2" data-testid="csv-preview">
                  <p className="text-xs font-bold">
                    {csvPreview.summary.to_place} to place · {csvPreview.summary.errors} error(s) · {csvPreview.summary.skipped} skipped
                  </p>
                  <div className="max-h-56 overflow-y-auto border border-gray-100 rounded-lg">
                    <table className="w-full text-xs">
                      <tbody>
                        {csvPreview.rows.map((r) => (
                          <tr key={r.line} className="border-b border-gray-50">
                            <td className="p-1.5 text-gray-400">{r.line}</td>
                            <td className="p-1.5 truncate max-w-[8rem]">{r.player_name || r.email}</td>
                            <td className="p-1.5 truncate max-w-[8rem]">{r.team}</td>
                            <td className={`p-1.5 font-bold ${r.status === 'ok' ? 'text-green-600' : r.status === 'error' ? 'text-red-500' : 'text-gray-400'}`}>
                              {r.status === 'ok' ? 'OK' : r.message}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button onClick={() => importCsv(false)} disabled={busy || csvPreview.summary.to_place === 0}
                    className="px-3 py-2 rounded-xl bg-[#3AAFA9] text-white font-bold text-xs disabled:opacity-50"
                    data-testid="csv-import-btn">
                    Place {csvPreview.summary.to_place} player(s){csvPreview.summary.errors ? ` (skip ${csvPreview.summary.errors} with errors)` : ''}
                  </button>
                </div>
              )}
            </div>
          </details>
        </div>
      )}
    </div>
  );
}
